import type { BeneficialOwnerResponse } from '@gcv/shared';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import { BeneficialOwnerApi } from 'api';
import { DISPENSARY_BENEFICIAL_OWNERS_KEY } from 'tanstack-hooks/keys';

export function useCrbSetManagingControlProng(dispensaryId: string) {
  const beneficialOwnerApi = new BeneficialOwnerApi();
  const queryClient = useQueryClient();

  const { mutate: setManagingControlProng, isPending: isSettingManagingControlProng } = useMutation({
    mutationKey: [DISPENSARY_BENEFICIAL_OWNERS_KEY, dispensaryId],
    mutationFn: ({
      beneficialOwnerId,
      templateResultId,
      confirmation
    }: {
      beneficialOwnerId: string;
      templateResultId: string;
      confirmation: boolean;
    }) =>
      beneficialOwnerApi.crbSetManagingControlProng(
        dispensaryId,
        beneficialOwnerId,
        templateResultId,
        confirmation
      ),
    onSuccess: (controlProngConfirmations) => {
      return queryClient.setQueryData(
        [DISPENSARY_BENEFICIAL_OWNERS_KEY, dispensaryId],
        (old: BeneficialOwnerResponse[] | undefined) =>
          old?.map((owner) => {
            const ownerConfirmations = controlProngConfirmations[owner.id];
            if (!ownerConfirmations) return owner;

            return {
              ...owner,
              template_result_map: Object.entries(ownerConfirmations).reduce(
                (templateResultMap, [templateResultId, confirmation]) => ({
                  ...templateResultMap,
                  [templateResultId]: {
                    ...templateResultMap[templateResultId],
                    confirmed_control_prong: confirmation.confirmed_control_prong
                  }
                }),
                owner.template_result_map
              )
            };
          })
      );
    },
    meta: {
      snackbarErrorMessage: 'There was an issue setting the managing individual.'
    }
  });

  return { setManagingControlProng, isSettingManagingControlProng };
}
